import { Injectable } from '@angular/core';
import { ApiServiceService } from './api-service.service';
import { Observable } from 'rxjs';
import { DoctorNPS } from '../interfaces/doctor-nps.interface';
import {
  CreateDoctorDTO,
  Doctor,
  DoctorListParams,
} from '../modules/admin/dto/doctor.dto';
import { HttpParams } from '@angular/common/http';
import { map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

type FindDoctorNpsDTO = {
  indicationId: number;
  doctorId: number;
};

type ApplyDoctorNpsDTO = {
  indicationId: number;
  doctorId: number;
  nps: number;
  comment: string;
};

export type DoctorFilter = {
  name: string;
  cpf: string;
};

export interface DoctorWithPaymentRowV2 {
  id: number | string;
  Nome: string;
  Ativo: 'ATIVO' | 'INATIVO';
  cpf: string;
  payment_wallet_id: string | null;
  Gateway: 'ASSAS' | null;
}

export type DoctorCrm = {
  crmNumber: number;
  uf: string;
  rqe: number[];
};

export type DoctorBySpecialty = {
  id: number;
  externalId: number;
  name: string;
  minimumAge: number | null;
  crm: DoctorCrm[];
};

export type DoctorGridItem = Pick<
  Doctor,
  'id' | 'name' | 'cpf' | 'isActive'
> & {
  payment_wallet_id: string | null;
};

@Injectable({ providedIn: 'root' })
export class DoctorService {
  constructor(private readonly apiService: ApiServiceService) {}

  applyNps({
    doctorId,
    indicationId,
    ...body
  }: ApplyDoctorNpsDTO): Observable<void> {
    return this.apiService.post(
      `/doctor/${doctorId}/nps/${indicationId}`,
      body
    );
  }

  findNps({ doctorId, indicationId }: FindDoctorNpsDTO): Observable<DoctorNPS> {
    return this.apiService.get(`/doctor/${doctorId}/nps/${indicationId}`);
  }

  findAll(
    _params: DoctorListParams
  ): Observable<{ list: DoctorGridItem[]; total: number }> {
    const url = `${environment.api_v2}/v2/administrative/doctors`;
    return this.apiService.get(url).pipe(
      map(
        (res: DoctorWithPaymentRowV2[] | { value: DoctorWithPaymentRowV2[] }) =>
          Array.isArray(res) ? res : res?.value ?? []
      ),
      map((rows: DoctorWithPaymentRowV2[]) => {
        const list: DoctorGridItem[] = rows.map((r) => ({
          id: Number(r.id),
          name: r.Nome,
          cpf: r.cpf,
          isActive: r.Ativo === 'ATIVO',
          payment_wallet_id: r.payment_wallet_id ?? null,
        }));
        return { list, total: list.length };
      })
    );
  }

  findByCpf(cpf: string): Observable<Doctor> {
    return this.apiService.get(`/doctor/cpf/${cpf}`);
  }

  findAllBySpecialty(specialtyId: number): Observable<DoctorBySpecialty[]> {
    const url = `${environment.api_v2}/api/doctor/find-by-specialty/${specialtyId}`;
    return this.apiService.get(url);
  }

  save(doctor: CreateDoctorDTO): Observable<Doctor> {
    return this.apiService.post('/doctor', doctor);
  }

  toggleActive(doctorId: number, active: boolean): Observable<void> {
    return this.apiService.put(`/doctor/${doctorId}/toggle-active`, { active });
  }

  search(filter: DoctorFilter) {
    const params = new HttpParams().set('filter', JSON.stringify(filter));
    return this.apiService.get('/doctor/combo', params).pipe(take(1));
  }

  syncAsaas(doctorId: number): Observable<void> {
    const url = `${environment.api_v2}/v2/administrative/doctors/${doctorId}/sync-asaas`;
    return this.apiService.post(url, {});
  }
}
